/**
 * Careers page (2026-10-08) — pure helpers: tracking params, salary text,
 * apply-form checks. The server re-validates everything; this is UX only.
 */
const SRC_ALLOW = ['linkedin', 'indeed', 'naukri', 'whatsapp', 'google'];
const CODE_RE = /^[A-HJ-NP-Z2-9]{10}$/;
const KEY = 'careers_tracking';

interface KV {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
}

export interface Tracking {
  src?: string;
  p?: string;
}

function safeStore(): KV | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** ?src / ?p from the URL (allow-listed), remembered for the visit so list → job → apply keeps them. */
export function readTracking(search: string, store: KV | null = safeStore()): Tracking {
  const q = new URLSearchParams(search || '');
  const src = (q.get('src') || '').trim().toLowerCase();
  const p = (q.get('p') || '').trim();
  const fresh: Tracking = {};
  if (CODE_RE.test(p)) fresh.p = p;
  else if (SRC_ALLOW.includes(src)) fresh.src = src;
  try {
    if (fresh.p || fresh.src) {
      store?.setItem(KEY, JSON.stringify(fresh));
      return fresh;
    }
    const saved = JSON.parse(store?.getItem(KEY) || '{}') as Tracking;
    const out: Tracking = {};
    if (saved.p && CODE_RE.test(saved.p)) out.p = saved.p;
    else if (saved.src && SRC_ALLOW.includes(saved.src)) out.src = saved.src;
    return out;
  } catch {
    return fresh;
  }
}

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function formatSalaryLakh(min: number | string | null | undefined, max: number | string | null | undefined): string {
  const a = num(min);
  const b = num(max);
  if (a !== null && b !== null) return `₹${a}–${b} L a year`;
  if (a !== null) return `From ₹${a} L a year`;
  if (b !== null) return `Up to ₹${b} L a year`;
  return '';
}

export function normalizeMobile(raw: string): string | null {
  let d = (raw || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return /^[6-9]\d{9}$/.test(d) ? d : null;
}

export interface CareerApplyInput {
  applicantName: string;
  phone: string;
  city: string;
}

export function validateCareerApply(f: CareerApplyInput): { ok: boolean; errors: Partial<Record<keyof CareerApplyInput, string>> } {
  const errors: Partial<Record<keyof CareerApplyInput, string>> = {};
  if (!f.applicantName.trim()) errors.applicantName = 'Please enter your name';
  else if (f.applicantName.trim().length > 120) errors.applicantName = 'Name is too long';
  else if (/[<>]/.test(f.applicantName)) errors.applicantName = 'Name has characters that are not allowed';
  if (!normalizeMobile(f.phone)) errors.phone = 'Enter a valid 10-digit mobile number';
  if (!f.city.trim()) errors.city = 'Please enter your city';
  return { ok: Object.keys(errors).length === 0, errors };
}
